import { z } from 'zod';
import { PHONEBOOKS, PHONEBOOK_NUMBER_KINDS, type PhonebookNumberKind } from './phonebook.js';

/**
 * Uploading contacts into a phonebook (docs/32 decision 3): a spreadsheet's CSV with its
 * columns matched by hand, vCards, and the phonebook XML Yealink and Fanvil phones use. Read in
 * the browser, sent as rows, checked again by the API.
 *
 * Sources: RFC 4180 (CSV); RFC 6350 and the 2.1 and 3.0 forms phones and Outlook still write
 * (vCard); Yealink's Remote Phone Book File Customization and contact export; Fanvil's XML
 * remote phonebook.
 */

/** A contact as read from a file, before its numbers are checked. */
export interface ImportedContact {
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  job_title: string | null;
  email: string | null;
  notes: string | null;
  numbers: Array<{ kind: PhonebookNumberKind; number: string }>;
}

const blank = (): ImportedContact => ({
  first_name: null, last_name: null, company: null, job_title: null, email: null, notes: null, numbers: [],
});

const clean = (v: string | null | undefined): string | null => {
  const t = (v ?? '').replace(/\s+/g, ' ').trim();
  return t === '' ? null : t;
};

/** Whether a contact read from a file has anything to keep. */
export const importable = (c: ImportedContact): boolean =>
  !!(c.first_name || c.last_name || c.company) && (c.numbers.length > 0 || !!c.email);

// ---------------------------------------------------------------------------------- CSV

/**
 * Rows of a CSV file: quoted fields with doubled quotes, commas and line breaks inside quotes,
 * CRLF or LF. The separator is whichever of comma, semicolon and tab the first line has most of
 * outside quotes, because spreadsheets in a comma-decimal locale save with semicolons.
 */
export function parseCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, '');
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const outside = firstLine.replace(/"[^"]*"/g, '');
  const counts = [',', ';', '\t'].map((d) => [d, outside.split(d).length - 1] as const);
  const sep = counts.reduce((a, b) => (b[1] > a[1] ? b : a))[0];

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field === '') quoted = true;
    else if (ch === sep) { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

/** What a CSV column can be matched to. */
export const CSV_FIELDS = [
  'full_name', 'first_name', 'last_name', 'company', 'job_title', 'email', 'notes',
  'work', 'mobile', 'home', 'other',
] as const;
export type CsvField = (typeof CSV_FIELDS)[number];

export const CSV_FIELD_LABEL: Readonly<Record<CsvField, string>> = {
  full_name: 'Full name', first_name: 'First name', last_name: 'Last name', company: 'Company',
  job_title: 'Job title', email: 'Email', notes: 'Notes',
  work: 'Work number', mobile: 'Mobile number', home: 'Home number', other: 'Other number',
};

/** Headers the common exports use (Outlook, Google, phones), for a first guess at the match. */
const HEADER_WORDS: ReadonlyArray<readonly [CsvField, RegExp]> = [
  ['first_name', /^(first ?name|given ?name|forename|name ?first)$/],
  ['last_name', /^(last ?name|surname|family ?name|name ?last)$/],
  ['full_name', /^(name|full ?name|display ?name|contact( ?name)?)$/],
  ['company', /^(company|organi[sz]ation( 1)?( - name)?|business|employer)$/],
  ['job_title', /^(job ?title|title|position|role|organi[sz]ation 1 - title)$/],
  ['email', /^(e-?mail( ?address)?( 1)?( - value)?|email 1)$/],
  ['notes', /^(notes?|comments?)$/],
  ['mobile', /^(mobile( ?(phone|number))?|cell( ?phone)?|cellular)$/],
  ['home', /^(home( ?(phone|number))?)$/],
  ['work', /^(work|office|business|company)( ?(phone|number|tel))?$|^(phone|telephone|tel|number|phone 1 - value)$/],
  ['other', /^(other( ?(phone|number))?|phone 2 - value|alternative)$/],
];

/** The field each column most likely is, or null. A field is given to one column at most. */
export function guessCsvColumns(header: readonly string[]): Array<CsvField | null> {
  const taken = new Set<CsvField>();
  return header.map((h) => {
    const word = h.trim().toLowerCase().replace(/[_]+/g, ' ');
    const hit = HEADER_WORDS.find(([field, re]) => !taken.has(field) && re.test(word));
    if (!hit) return null;
    taken.add(hit[0]);
    return hit[0];
  });
}

/**
 * Contacts from CSV rows, each column matched to a field (or to nothing). A full name is split
 * at its last space when neither first nor last name is matched.
 */
export function contactsFromCsv(
  rows: readonly string[][], columns: ReadonlyArray<CsvField | null>,
): ImportedContact[] {
  return rows.map((row) => {
    const c = blank();
    let full = '';
    for (const [i, field] of columns.entries()) {
      const value = clean(row[i]);
      if (!field || value === null) continue;
      switch (field) {
        case 'full_name': full = value; break;
        case 'work': case 'mobile': case 'home': case 'other':
          for (const part of value.split(/\s*[;|]\s*/)) {
            if (part) c.numbers.push({ kind: field, number: part });
          }
          break;
        default: c[field] = c[field] ?? value;
      }
    }
    if (full && !c.first_name && !c.last_name) {
      const at = full.lastIndexOf(' ');
      if (at > 0) {
        c.first_name = full.slice(0, at);
        c.last_name = full.slice(at + 1);
      } else c.first_name = full;
    }
    return c;
  });
}

// -------------------------------------------------------------------------------- vCard

const unescapeVcard = (v: string) =>
  v.replace(/\\([nN,;\\])/g, (_, ch: string) => (ch === 'n' || ch === 'N' ? '\n' : ch));

const quotedPrintable = (v: string) =>
  decodeURIComponent(v.replace(/=\r?\n/g, '').replace(/%/g, '%25').replace(/=([0-9A-F]{2})/gi, '%$1'));

function vcardNumberKind(params: string): PhonebookNumberKind {
  const p = params.toLowerCase();
  if (/cell|mobile|iphone/.test(p)) return 'mobile';
  if (/home/.test(p)) return 'home';
  if (/work|main|pref|voice/.test(p) && !/fax/.test(p)) return 'work';
  return 'other';
}

/** Every card in a .vcf file. */
export function parseVcards(input: string): ImportedContact[] {
  // Unfold: a line that starts with a space or tab continues the one before it.
  const lines = input.replace(/^\uFEFF/, '').split(/\r?\n/).reduce<string[]>((out, line) => {
    if (/^[ \t]/.test(line) && out.length > 0) out[out.length - 1] += line.slice(1);
    else out.push(line);
    return out;
  }, []);

  const cards: ImportedContact[] = [];
  let c: ImportedContact | null = null;
  let fn: string | null = null;
  for (const line of lines) {
    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const head = line.slice(0, colon);
    let value = line.slice(colon + 1);
    // "item1.TEL;type=CELL" (Apple groups) -> name TEL, params type=CELL
    const [nameWithGroup = '', ...paramList] = head.split(';');
    const name = nameWithGroup.split('.').pop()!.toUpperCase();
    const params = paramList.join(';');
    if (/encoding=quoted-printable/i.test(params)) value = quotedPrintable(value);

    if (name === 'BEGIN' && /^vcard$/i.test(value.trim())) { c = blank(); fn = null; continue; }
    if (!c) continue;
    switch (name) {
      case 'END':
        if (!c.first_name && !c.last_name && fn) {
          const at = fn.lastIndexOf(' ');
          if (at > 0) { c.first_name = fn.slice(0, at); c.last_name = fn.slice(at + 1); } else c.first_name = fn;
        }
        cards.push(c);
        c = null;
        break;
      case 'N': {
        const [family = '', given = ''] = value.split(';').map(unescapeVcard);
        c.last_name = clean(family);
        c.first_name = clean(given);
        break;
      }
      case 'FN': fn = clean(unescapeVcard(value)); break;
      case 'ORG': c.company = c.company ?? clean(unescapeVcard(value.split(';')[0] ?? '')); break;
      case 'TITLE': c.job_title = c.job_title ?? clean(unescapeVcard(value)); break;
      case 'EMAIL': c.email = c.email ?? clean(value); break;
      case 'NOTE': c.notes = c.notes ?? clean(unescapeVcard(value)); break;
      case 'TEL': {
        if (/fax|pager/i.test(params)) break;
        const number = clean(value.replace(/^tel:/i, '').replace(/;ext=.*$/i, ''));
        if (number) c.numbers.push({ kind: vcardNumberKind(params), number });
        break;
      }
      default:
    }
  }
  return cards;
}

// ---------------------------------------------------------------------------------- XML

const ENTITIES: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

const xmlText = (v: string) => v
  .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
  .replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (whole, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[e.toLowerCase()] ?? whole;
  });

const attrs = (tag: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const m of tag.matchAll(/([\w:.-]+)\s*=\s*("([^"]*)"|'([^']*)')/g)) {
    out[m[1]!.toLowerCase()] = xmlText(m[3] ?? m[4] ?? '');
  }
  return out;
};

const children = (body: string): Array<{ name: string; attrs: Record<string, string>; text: string }> =>
  [...body.matchAll(/<([\w:.-]+)([^>]*?)(?:\/>|>([\s\S]*?)<\/\1\s*>)/g)].map((m) => ({
    name: m[1]!.toLowerCase(), attrs: attrs(m[2] ?? ''), text: xmlText(m[3] ?? '').trim(),
  }));

function telephoneKind(label: string | undefined, index: number): PhonebookNumberKind {
  const l = (label ?? '').toLowerCase();
  if (/mob|cell/.test(l)) return 'mobile';
  if (/home/.test(l)) return 'home';
  if (/office|work|business/.test(l)) return 'work';
  return index === 0 ? 'work' : 'other';
}

/**
 * Contacts in a phonebook XML file:
 * - `DirectoryEntry` with `Name` and `Telephone` (Yealink's remote phonebook; Fanvil's adds
 *   `Mobile` and `Other`);
 * - `Unit` with Name and Phone1 to Phone3 (Yealink's menu phonebook);
 * - `contact` with display_name, office_number, mobile_number and other_number (a Yealink
 *   phone's own export).
 * A display name is kept whole as the first name: a file does not say where a name splits.
 */
export function parsePhonebookXml(input: string): ImportedContact[] {
  const out: ImportedContact[] = [];
  const text = input.replace(/<!--[\s\S]*?-->/g, '');

  for (const m of text.matchAll(/<DirectoryEntry\b[^>]*>([\s\S]*?)<\/DirectoryEntry\s*>/gi)) {
    const c = blank();
    let telephones = 0;
    for (const child of children(m[1] ?? '')) {
      if (!child.text) continue;
      switch (child.name) {
        case 'name': c.first_name = c.first_name ?? clean(child.text); break;
        case 'telephone': c.numbers.push({ kind: telephoneKind(child.attrs['label'], telephones++), number: child.text }); break;
        case 'mobile': c.numbers.push({ kind: 'mobile', number: child.text }); break;
        case 'other': c.numbers.push({ kind: 'other', number: child.text }); break;
        case 'office': c.numbers.push({ kind: 'work', number: child.text }); break;
        case 'email': c.email = c.email ?? clean(child.text); break;
        default:
      }
    }
    out.push(c);
  }

  for (const m of text.matchAll(/<Unit\b([^>]*)\/?>/gi)) {
    const a = attrs(m[1] ?? '');
    const c = blank();
    c.first_name = clean(a['name']);
    (['phone1', 'phone2', 'phone3'] as const).forEach((k, i) => {
      const number = clean(a[k]);
      if (number) c.numbers.push({ kind: (['work', 'mobile', 'other'] as const)[i]!, number });
    });
    out.push(c);
  }

  for (const m of text.matchAll(/<contact\b([^>]*)\/?>/gi)) {
    const a = attrs(m[1] ?? '');
    if (!('display_name' in a)) continue;
    const c = blank();
    c.first_name = clean(a['display_name']);
    for (const [k, kind] of [['office_number', 'work'], ['mobile_number', 'mobile'], ['other_number', 'other']] as const) {
      const number = clean(a[k]);
      if (number) c.numbers.push({ kind, number });
    }
    out.push(c);
  }
  return out;
}

/** Which reader a file needs, by its name and then by what it starts with. */
export function importKind(fileName: string, head: string): 'csv' | 'vcard' | 'xml' | null {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  if (ext === 'vcf' || ext === 'vcard') return 'vcard';
  if (ext === 'xml') return 'xml';
  if (ext === 'csv' || ext === 'txt') return /^\uFEFF?\s*BEGIN:VCARD/i.test(head) ? 'vcard' : 'csv';
  if (/^\uFEFF?\s*BEGIN:VCARD/i.test(head)) return 'vcard';
  if (/^\uFEFF?\s*</.test(head)) return 'xml';
  return null;
}

// ------------------------------------------------------------------------------ the API

const importText = (max: number) => z.string().trim().max(max).nullable().optional()
  .transform((v) => (v ? v : null));

/** One batch of an upload, as the API takes it. The browser sends a large file in batches. */
export const PhonebookImportSchema = z.object({
  book: z.enum(PHONEBOOKS),
  department_id: z.string().uuid().nullable().optional().transform((v) => v ?? null),
  extension_id: z.string().uuid().nullable().optional().transform((v) => v ?? null),
  contacts: z.array(z.object({
    first_name: importText(80),
    last_name: importText(80),
    company: importText(120),
    job_title: importText(80),
    email: importText(254),
    notes: importText(2000),
    numbers: z.array(z.object({
      kind: z.enum(PHONEBOOK_NUMBER_KINDS),
      number: z.string().max(40),
    })).max(8).default([]),
  })).min(1).max(500),
}).strict();
export type PhonebookImport = z.input<typeof PhonebookImportSchema>;

export interface PhonebookImportResult {
  created: number;
  companies_created: number;
  /** Rows with no name and no company. */
  skipped: number;
  /** Numbers that were not numbers, or past a contact's eighth. */
  numbers_dropped: number;
  /** Company names left off because the account has no such company and the caller may not add one. */
  companies_dropped: number;
}

/** The most a browser sends in one request. */
export const PHONEBOOK_IMPORT_BATCH = 500;
